import clsx from 'clsx';
import {
  Activity,
  BellRing,
  Camera,
  ChartLine,
  LayoutDashboard,
  ListFilter,
  LogOut,
  Menu,
  Settings,
  Shapes,
  Users,
  Video,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRealtimeSync } from '../hooks/useRealtime';
import { ConnectionBadge } from './ui';
import type { Permission } from '@playplate/shared';

type NavItem = { to: string; label: string; icon: typeof Camera; permission?: Permission };

const NAV: NavItem[] = [
  { to: '/', label: 'Command Center', icon: LayoutDashboard },
  { to: '/cameras', label: 'Cameras', icon: Camera, permission: 'camera:read' },
  { to: '/live', label: 'Live View', icon: Video, permission: 'camera:stream' },
  { to: '/events', label: 'Events', icon: ListFilter, permission: 'event:read' },
  { to: '/alerts', label: 'Alerts', icon: BellRing, permission: 'alert:read' },
  { to: '/analytics', label: 'Analytics', icon: ChartLine, permission: 'analytics:read' },
  { to: '/zones', label: 'Zones', icon: Shapes, permission: 'zone:read' },
  { to: '/users', label: 'Users & Roles', icon: Users, permission: 'user:read' },
  { to: '/system', label: 'System Health', icon: Activity, permission: 'system:read' },
  { to: '/settings', label: 'Settings', icon: Settings, permission: 'settings:read' },
];

export function Shell() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // Mounted here, once, for the whole authenticated app: the socket must outlive
  // any individual screen.
  const { status } = useRealtimeSync();

  // The nav only lists what this role can actually reach. It is a convenience,
  // not a control — every route and every API call re-checks server-side.
  const visible = NAV.filter((item) => !item.permission || can(item.permission));

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-line bg-panel/95 backdrop-blur',
          'transition-transform lg:static lg:translate-x-0',
          menuOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div>
            <p className="font-display text-sm font-extrabold tracking-[0.18em] text-lime">PLAYPLATE</p>
            <p className="font-display text-xs tracking-[0.22em] text-muted">AI VISION</p>
          </div>
          <button
            type="button"
            className="btn-ghost p-1 lg:hidden"
            onClick={() => setMenuOpen(false)}
            aria-label="Close navigation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent/15 text-paper ring-1 ring-inset ring-accent/40'
                    : 'text-muted hover:bg-panel-raised hover:text-paper',
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" aria-hidden />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-line p-3">
          <div className="rounded-lg bg-panel-raised px-3 py-2">
            <p className="truncate text-sm font-semibold">{user?.name}</p>
            <p className="truncate text-xs text-muted">{user?.role}</p>
          </div>
          <button type="button" className="btn-ghost mt-2 w-full justify-start" onClick={handleLogout}>
            <LogOut className="h-4 w-4" aria-hidden />
            Sign out
          </button>
        </div>
      </aside>

      {menuOpen ? (
        <div
          className="fixed inset-0 z-30 bg-ink/70 lg:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden
        />
      ) : null}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-line bg-ink/80 px-4 py-3 backdrop-blur lg:px-8">
          <button
            type="button"
            className="btn-ghost p-2 lg:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="ml-auto flex items-center gap-4">
            <ConnectionBadge status={status} />
          </div>
        </header>

        <main className="flex-1 px-4 py-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
